import Alpine from './alpine'

/**
 * The draft-order line builder.
 *
 * Totals are computed in the browser so the merchant sees the effect of a
 * quantity or discount change immediately; the server recomputes all of it on
 * save and its answer is the one that counts.
 */
Alpine.data('draftOrderBuilder', (config = {}) => ({
    lines: config.lines ?? [],
    currency: config.currency ?? 'USD',
    variantSearchUrl: config.variantSearchUrl ?? '',
    customerSearchUrl: config.customerSearchUrl ?? '',

    picker: { open: false, query: '', results: [], loading: false, timer: null },
    customerPicker: { open: false, query: '', results: [], loading: false, timer: null },

    customer: config.customer ?? null,

    discount: config.discount ?? { title: '', value: '', value_type: 'fixed_amount' },
    shipping: config.shipping ?? { title: '', price: '' },
    tax: config.tax ?? '',

    // Lines -------------------------------------------------------------

    addCustomLine() {
        this.lines.push({
            variant_id: null,
            title: '',
            variant_title: null,
            sku: '',
            price: '0.00',
            quantity: 1,
            image: null,
            custom: true,
        })
    },

    addVariant(variant) {
        const existing = this.lines.find((line) => line.variant_id === variant.variant_id)

        if (existing) {
            existing.quantity = Number(existing.quantity) + 1
        } else {
            this.lines.push({ ...variant, quantity: 1 })
        }

        this.picker.open = false
        this.picker.query = ''
        this.picker.results = []
    },

    removeLine(index) {
        this.lines.splice(index, 1)
    },

    // Product picker ------------------------------------------------------

    openPicker() {
        this.picker.open = true
        this.searchVariants()
        this.$nextTick(() => this.$refs.pickerInput?.focus())
    },

    searchVariants() {
        clearTimeout(this.picker.timer)
        this.picker.timer = setTimeout(async () => {
            this.picker.loading = true
            try {
                const response = await fetch(
                    `${this.variantSearchUrl}?q=${encodeURIComponent(this.picker.query)}`,
                    { headers: { Accept: 'application/json' } }
                )
                this.picker.results = response.ok ? await response.json() : []
            } catch {
                this.picker.results = []
            } finally {
                this.picker.loading = false
            }
        }, 250)
    },

    // Customer picker -----------------------------------------------------

    searchCustomers() {
        clearTimeout(this.customerPicker.timer)

        if (this.customerPicker.query.trim() === '') {
            this.customerPicker.results = []
            return
        }

        this.customerPicker.timer = setTimeout(async () => {
            this.customerPicker.loading = true
            try {
                const response = await fetch(
                    `${this.customerSearchUrl}?q=${encodeURIComponent(this.customerPicker.query)}`,
                    { headers: { Accept: 'application/json' } }
                )
                this.customerPicker.results = response.ok ? await response.json() : []
            } catch {
                this.customerPicker.results = []
            } finally {
                this.customerPicker.loading = false
            }
        }, 250)
    },

    selectCustomer(customer) {
        this.customer = customer
        this.customerPicker.open = false
        this.customerPicker.query = ''
        this.customerPicker.results = []
    },

    clearCustomer() {
        this.customer = null
    },

    // Totals ---------------------------------------------------------------

    get subtotal() {
        return this.lines.reduce(
            (sum, line) => sum + (parseFloat(line.price) || 0) * (parseInt(line.quantity) || 0),
            0
        )
    },

    get discountAmount() {
        const value = parseFloat(this.discount.value)
        if (isNaN(value) || value <= 0) return 0

        return this.discount.value_type === 'percentage'
            ? (this.subtotal * value) / 100
            : Math.min(value, this.subtotal)
    },

    get shippingAmount() {
        return parseFloat(this.shipping.price) || 0
    },

    get taxAmount() {
        return parseFloat(this.tax) || 0
    },

    get total() {
        return Math.max(0, this.subtotal - this.discountAmount) + this.shippingAmount + this.taxAmount
    },

    money(amount) {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: this.currency,
        }).format(amount || 0)
    },

    get isEmpty() {
        return this.lines.length === 0
    },
}))
